import { useEffect, useRef, useState } from 'react';
import { Axis3d, ChevronDown, ChevronUp, Home } from 'lucide-react';
import { AXES, AxisFields, AxisLetters, AxisRowTitle, asNumber } from './axisGrid';
import { useLanguage } from '../../contexts/LanguageContext';

// HOW FAR, on the menu each letter opens. Two turns, and they are the two that ARRIVE
// somewhere: a quarter brings the next side of the part to face you, a half brings the back.
// Anything between them is a look-around, which is what dragging is for.
//
// The menu is what a viewer with NO READINGS gets — the mesh bench, three letters and nothing
// under them. Where the angles are printed they are typed into instead: a step is a way of
// getting somewhere from where you are, and a number is the place itself.
const TURNS = [90, 180];

// A TURN THAT ARRIVES INSTEAD OF BEING DRAGGED, and the way back to where the view opened.
//
// A drag is the right way to look for something and the wrong way to reach somewhere known.
// "The other side" and "back to how it opened" are two places anybody working on a part goes
// over and over, and by hand each is a wrist's worth of orbiting that lands somewhere NEAR.
//
// Shared by the mesh viewer and the assembly phase. What each does with a turn differs — one
// swings about the world's axes, the other about the assembly's, which is what its own gizmo
// is labelled in — but that is behind `onTurn`, and the control is the same control.
export function ViewTurnCorner({
  onHome, onTurn, onLook, className = '', homeLabel, angles, centre, zoom, gizmoOn,
  onToggleGizmo,
}: {
  onHome: () => void;
  // What the house does, in words, for a viewer whose home is not "the view you started on".
  // The assembly's squares the angle up rather than restoring the opening shot, and a tooltip
  // that says otherwise is worse than none: it is the only place the button explains itself.
  homeLabel?: string;
  onTurn: (axis: 'x' | 'y' | 'z', degrees: number) => void;
  className?: string;
  // WHERE THE VIEW IS NOW, about each of these same three axes, in degrees off square.
  //
  // Under the letter it belongs to, which is the whole reason it lives in this control rather
  // than in a box of its own beside it. The letters and the numbers were the same X, Y and Z
  // written twice, a few pixels apart, in the same three colours — and the pairing says the
  // thing neither said alone: this is the axis, that is how far you are turned about it, and
  // the button above the number is what changes it.
  //
  // Optional, because the mesh viewer shares this control and has no such reading to give.
  // Absent, the row is exactly what it was.
  angles?: [number, number, number];
  // WHERE THE SHOT IS CENTRED, in the assembly's frame and in centimetres — zero when the
  // gizmo sits in the middle of the picture, and drifting as the view is panned.
  //
  // Between the letter and the angle, because it is the other half of "where am I looking":
  // the angle says which way the camera faces about this arm, and this says how far along it
  // the camera is aimed. Quieter than the angle — lighter, not bold, no unit — since it is a
  // reading and the angle beneath it is a control.
  centre?: [number, number, number];
  // AND HOW TO CHANGE EITHER: put the shot exactly there — the six numbers as they are
  // printed, centimetres and degrees, both rows at once.
  //
  // ABSOLUTE AND NOT A STEP, which is the whole of why it replaced the menus that hung off
  // these numbers. "+10" and "+90" are instructions about where you already are; a person
  // reading a row that says 34 and wanting 0 was pressing −10 three times and arriving at 4.
  // Both rows go together because they are one shot, and because the reading is taken of the
  // camera as a whole — sending half of it would aim a camera that had not turned.
  //
  // Absent, the two rows are readouts and nothing more, which is what the mesh bench wants.
  onLook?: (view: {
    centre: [number, number, number]; deg: [number, number, number];
  }) => void;
  // HOW FAR IN, as a multiple of the home shot — and it goes UNDER THE HOUSE, which is the
  // whole reason it reads as anything. x1 is not a fact about the world; it is a fact about
  // that button. Printed beneath it, the number says what it is measured against and the
  // button says how to get back to it, which is the same pairing the angles have with their
  // letters. Anywhere else in this box it would be a bare number wanting a footnote.
  zoom?: number;
  // THE GIZMO'S OWN SWITCH, under the house. Both are about the frame the picture is read in
  // rather than about anything in it — where the origin is and how to get back to looking at
  // it — so they belong in the same column, and the zoom between them is the third fact of
  // that kind. Absent, no button is drawn: the mesh viewer shares this control and has no
  // gizmo standing in its scene to turn off.
  gizmoOn?: boolean;
  onToggleGizmo?: () => void;
}) {
  const { t } = useLanguage();
  // WHICH NUMBER HAS ITS MENU OPEN — the row as well as the axis, now that two rows open one.
  // One piece of state and not two, because only one menu may stand at a time: two would let
  // the position's and the angle's overlap in a box this small, and each closes the other by
  // being what `open` now is.
  const [open, setOpen] = useState<'x' | 'y' | 'z' | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [folded, setFolded] = useState(readoutFolded);
  const fold = (on: boolean) => { readoutFolded = on; setFolded(on); };

  // WHAT IS IN THE BOXES, which is not the same thing as where the camera is. It follows the
  // camera through every frame of an orbit — that is what a readout is — and it stops
  // following the moment somebody types, because up to the commit the half-typed number is
  // theirs. The same arrangement the coordinates panel makes with a part being dragged.
  const [aimed, setAimed] = useState<string[]>(['0', '0', '0']);
  const [spun, setSpun] = useState<string[]>(['0', '0', '0']);
  const [edited, setEdited] = useState(false);
  // KEYED ON THE VALUES AND NOT THE ARRAYS. A new triple arrives on every frame of a drag,
  // equal to the last one for most of them; depending on the arrays would re-set three
  // strings sixty times a second to say nothing had changed.
  const posKey = (centre ?? []).join(',');
  const rotKey = (angles ?? []).join(',');
  useEffect(() => {
    if (edited) return;
    setAimed((centre ?? [0, 0, 0]).map(String));
    setSpun((angles ?? [0, 0, 0]).map(String));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posKey, rotKey, edited]);

  // WHAT IS IN THEM, SENT — both rows, whichever one was typed in. Taken as arguments rather
  // than read out of the state, because a commit fires from inside the same event that set it
  // and the state is still one render behind.
  const commit = (at: string[], turned: string[]) => {
    // NOTHING TYPED, NOTHING SENT. Every one of these fields blurs the moment the pointer
    // goes back to the viewport, and a commit of the untouched reading is not the no-op it
    // looks like: the row is printed rounded — whole degrees, tenths of a centimetre — so
    // re-aiming the camera at what it says would snap the shot a little every time somebody
    // clicked a field and clicked away.
    if (!edited) return;
    setEdited(false);
    onLook?.({
      centre: at.map(asNumber) as [number, number, number],
      deg: turned.map(asNumber) as [number, number, number],
    });
  };

  // The reading put back, for a number somebody started and thought better of. Cheaper here
  // than anywhere else: the camera never stopped being where it is, so this is the readout
  // resuming rather than an undo.
  const revert = () => {
    setEdited(false);
    setAimed((centre ?? [0, 0, 0]).map(String));
    setSpun((angles ?? [0, 0, 0]).map(String));
  };

  // A MENU OVER A VIEWPORT HAS TO SHUT ON THE VIEWPORT. The next thing anybody does here is
  // drag the object, and a press that starts a drag must not also have to be a press that
  // dismisses a menu — so it closes on any pointer down outside the control, in the CAPTURE
  // phase, before the canvas has taken the event. Escape closes it too, as everything else
  // over these viewers does.
  useEffect(() => {
    if (!open) return;
    const shut = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(null);
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    document.addEventListener('pointerdown', shut, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', shut, true);
      document.removeEventListener('keydown', key);
    };
  }, [open]);


  // ONE COLUMN PER AXIS, or one ROW per reading — the same picture read the other way, and
  // the titles are what force the choice. A heading belongs over the row it names, and a row
  // here crosses all three axes; three columns each carrying their own copy of "camera
  // position" would say it three times and line up none of them.
  //
  // So the axes are the columns and the readings are the rows, with a title spanning the
  // three above each. `min-w-0` because a text input's default width is wider than this and
  // would push the grid past the edge of the box it sits in.
  const cell = 'w-14 min-w-0 text-center';

  // THE LETTER AS THE HANDLE, where there is no reading to type into: press X and the turns
  // hang under it. Only that one branch has this now — a printed angle is typed into instead
  // — so what was a builder shared by two rows is one letter and one menu.
  const trigger = (axis: 'x' | 'y' | 'z', colour: string, title: string) => (
    <button
      type="button"
      onClick={() => setOpen((was) => (was === axis ? null : axis))}
      title={title}
      aria-label={title}
      aria-haspopup="menu"
      aria-expanded={open === axis}
      style={{ color: colour }}
      className={`w-full rounded py-1 text-center text-[10px] font-bold leading-none tabular-nums transition-colors hover:bg-slate-100 ${
        open === axis ? 'bg-slate-100' : ''
      }`}
    >
      {axis.toUpperCase()}
    </button>
  );

  // The steps on offer, signed, with the sign written out — a menu of "10" and "10" would be
  // two identical items. A true minus rather than a hyphen, because these sit under a column
  // of numbers that may already carry one.
  const menu = (
    axis: 'x' | 'y' | 'z',
    colour: string,
    steps: number[],
    apply: (step: number) => void,
    unit: string,
  ) => (open === axis ? (
    // Centred on the column it belongs to: the menu is about that arrow, and an offset box
    // points at the column beside it.
    <div
      role="menu"
      className="absolute left-1/2 top-full z-10 mt-1 flex -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-slate-300 bg-white shadow-lg"
    >
      {steps.map((step) => (
        <button
          key={step}
          type="button"
          role="menuitem"
          onClick={() => { apply(step); setOpen(null); }}
          style={{ color: colour }}
          className="whitespace-nowrap px-2.5 py-1.5 text-center text-[11px] font-bold leading-none tabular-nums transition-colors hover:bg-slate-100"
        >
          {step < 0 ? '−' : '+'}{Math.abs(step)}{unit}
        </button>
      ))}
    </div>
  ) : null);

  // FOLDED UP BY ITS OWN TOP ROW where it carries the readings, as the parts list is (see
  // ScenePartCards' foldRow): folded, that row is all that is left of it.
  const foldable = !!angles;
  const foldRow = foldable ? (
    <button
      type="button"
      onClick={() => fold(!folded)}
      aria-expanded={!folded}
      className={`flex w-full items-center justify-center py-0.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600 ${folded ? '' : 'border-b border-slate-200'}`}
    >
      {folded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
    </button>
  ) : null;
  if (foldable && folded) {
    return <div className={`w-24 overflow-hidden rounded-lg border border-slate-300 bg-white/90 shadow-sm backdrop-blur ${className}`}>{foldRow}</div>;
  }
  return (
    <div className={`overflow-hidden rounded-lg border border-slate-300 bg-white/90 shadow-sm backdrop-blur ${className}`}>
    {foldRow}
    <div
      ref={box}
      className="flex items-center gap-1 p-1"
    >
      {/* THE FRAME'S OWN COLUMN, centred on the rows beside it. Two buttons and a reading,
          against five rows of axes: aligned to the top it read as a header for a table it is
          not part of. `justify-center` says so even where the outer alignment already does,
          because this column is the thing that will grow next. */}
      <div className="flex flex-col items-center justify-center self-center">
        <button
          type="button"
          onClick={onHome}
          title={homeLabel ?? t('notice.3d.viewHome')}
          aria-label={homeLabel ?? t('notice.3d.viewHome')}
          className="rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-blue-600"
        >
          <Home className="h-3.5 w-3.5" />
        </button>
        {zoom != null && (
          <span
            title={t('notice.3d.viewZoom')}
            className="w-10 text-center text-[10px] leading-none tabular-nums text-slate-500"
          >
            {/* Two decimals up to ten, none past it: at x14 the hundredths are noise, and at
                x1.05 they are the whole of what changed. */}
            ×{zoom < 10 ? zoom.toFixed(2) : Math.round(zoom)}
          </span>
        )}
        {onToggleGizmo && (
          <button
            type="button"
            onClick={onToggleGizmo}
            title={t(gizmoOn ? 'notice.assembly.hideGizmo' : 'notice.assembly.showGizmo')}
            aria-label={t(gizmoOn ? 'notice.assembly.hideGizmo' : 'notice.assembly.showGizmo')}
            aria-pressed={gizmoOn}
            className="mt-0.5 rounded p-1 transition-colors hover:bg-slate-100"
          >
            <Axis3d
              className={`h-3.5 w-3.5 ${
                gizmoOn
                  ? 'text-blue-600'
                  : 'text-slate-400'
              }`}
            />
          </button>
        )}
      </div>
      <span className={`w-px bg-slate-300 ${angles ? 'h-16' : 'h-4'}`} />

      {angles ? (
        <div className="flex flex-col">
          <AxisLetters cell={cell} />

          {centre && (
            <>
              {/* WHERE THE SHOT IS AIMED, typed — in centimetres along the assembly's own arms. A
                  drag on the background moves it too. Typing an angle sends this row's numbers
                  unchanged, so it never moves. */}
              <AxisRowTitle>{t('notice.3d.viewPosRow')}</AxisRowTitle>
              <AxisFields
                values={aimed}
                cell={cell}
                onChange={(next) => { setEdited(true); setAimed(next); }}
                onCommit={(next) => commit(next, spun)}
                onRevert={revert}
              />
            </>
          )}

          <AxisRowTitle>{t('notice.3d.viewRotRow')}</AxisRowTitle>
          {/* The degree sign is off the numbers because the heading over them carries it.
              Stated twice, three columns wide, it was three characters of noise in the row
              that has the least room. */}
          <AxisFields
            values={spun}
            cell={cell}
            onChange={(next) => { setEdited(true); setSpun(next); }}
            onCommit={(next) => commit(aimed, next)}
            onRevert={revert}
          />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          {AXES.map(({ axis, colour }) => (
            <div key={axis} className="relative">
              {trigger(axis, colour, t('notice.3d.viewTurn').replace('{axis}', axis.toUpperCase()))}
              {menu(axis, colour, TURNS, (deg) => onTurn(axis, deg), '°')}
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
}

// Folded or not, the same for every viewer on the page and from one sub-step to the next.
let readoutFolded = false;
